import {
  createTheme,
  Theme as DefaultTheme,
  ThemeOptions,
  lighten,
  darken,
  decomposeColor,
  recomposeColor,
  alpha,
} from "@material-ui/core/styles";
import { getContrastText, getContrastRatio } from "./utils/colorUtils";

// ============================================
// BRAND COLORS (preserved from original theme)
// ============================================
const lightColor = "rgba(241, 238, 255, 0.7)";
const lightGrayColor = "rgb(241, 238, 245)";
const lightBlueColor = "rgb(68, 112, 147)";
const blueColor = "rgb(24, 68, 97)";
const lightGreenColor = "rgb(28, 93, 59)";
const greenColor = "rgb(4, 76, 38)";
const errorColor = "rgb(255, 69, 58)";
const warningColor = "#FFFAA0";

// ============================================
// SHADOW COLORS
// ============================================
// Light mode: dark shadows for depth
// Dark mode: light shadows for glow/elevation effect
const SHADOW_COLOR_LIGHT = "0, 0, 0";
const SHADOW_COLOR_DARK = "255, 255, 255";

// Legacy exports for backward compatibility
export const colors = {
  blueColor,
  lightColor,
  lightGrayColor,
  lightBlueColor,
  lightGreenColor,
  greenColor,
  errorColor,
};

const Colors = {
  blue: blueColor,
  light: lightColor,
  lightGray: lightGrayColor,
  lightBlue: lightBlueColor,
  lightGreen: lightGreenColor,
  green: greenColor,
  error: errorColor,
};

// ============================================
// CUSTOMIZABLE PALETTE INTERFACE (7 colors)
// ============================================
export interface CustomPalette {
  bg: string; // Page background
  card: string; // Card/surface background
  text: string; // Primary text color
  border: string; // Borders/dividers
  accent: string; // UI accent (buttons, links)
  brandPrimary: string; // Charts primary (blue family)
  brandSecondary: string; // Charts secondary (green family)
}

// ============================================
// DEFAULT PALETTES
// ============================================
export const defaultLightPalette: CustomPalette = {
  bg: "#f8f9f8",
  card: "#ffffff",
  text: "rgba(0, 0, 0, 0.87)",
  border: "rgba(0, 0, 0, 0.12)",
  accent: "#447093",
  brandPrimary: "#184461",
  brandSecondary: "#044c26",
};

export const defaultDarkPalette: CustomPalette = {
  bg: "#121212",
  card: "#0d1c13",
  text: "rgba(255, 255, 255, 0.87)",
  border: "rgba(255, 255, 255, 0.12)",
  accent: "#4dff91",
  brandPrimary: "#2d6a91",
  brandSecondary: "#3a7d56",
};

// ============================================
// DERIVED COLOR UTILITIES
// ============================================
function deriveSecondaryText(textColor: string): string {
  const color = decomposeColor(textColor);
  const [r, g, b] = color.values;
  switch (color.type) {
    case "rgb":
      return recomposeColor({ type: "rgba", values: [r, g, b, 0.702] });
    case "rgba":
      return recomposeColor({
        type: "rgba",
        values: [r, g, b, (color.values[3] ?? 1) * 0.7],
      });
    default:
      throw new Error(`Unsupported color type: ${color.type} [${textColor}]`);
  }
}

function deriveShadow(
  isDark: boolean,
  elevation: "xs" | "sm" | "md" | "lg"
): string {
  // Dark mode uses white shadows for a subtle glow effect
  // Light mode uses traditional black shadows
  const alphas = isDark
    ? { xs: 0.05, sm: 0.08, md: 0.1, lg: 0.12 }
    : { xs: 0.08, sm: 0.12, md: 0.1, lg: 0.15 };

  const sizes = {
    xs: "0 1px 3px",
    sm: "0 2px 4px",
    md: "0 4px 8px",
    lg: "0 8px 16px",
  };

  const color = isDark ? SHADOW_COLOR_DARK : SHADOW_COLOR_LIGHT;
  return `${sizes[elevation]} rgba(${color}, ${alphas[elevation]})`;
}

function deriveBrandLight(brandColor: string): string {
  return lighten(brandColor, 0.3);
}

function deriveBorderColor(color: string): string {
  // User-saved colors are always hex, so just handle that case
  // Default rgba borders already have 12% opacity
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
  }
  return color; // Keep rgba defaults as-is
}

// ============================================
// EXTENDED THEME INTERFACE
// ============================================
export type ThemeMode = "light" | "dark";

export type ThemeAppearance = "custom" | "default";

export interface CustomThemeExtension {
  mode: ThemeMode;
  appearance: ThemeAppearance;
  customDarkBg: string;
  custom: CustomPalette;
  derived: {
    textSecondary: string;
    shadow: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
    };
    brandPrimaryLight: string;
    brandSecondaryLight: string;
    // Auto-calculated readable text colors for each surface
    onBg: string; // Readable text on page background
    onCard: string; // Readable text on card/paper background
    onAccent: string; // Readable text on accent color
    onPrimary: string; // Readable text on brandPrimary
    onSecondary: string; // Readable text on brandSecondary
    // Semantic colors with 3:1 contrast fallback to text colors
    accentOnBg: string; // Accent color on bg (links, outlined buttons)
    accentOnCard: string; // Accent color on card (icons, tooltips)
    titleOnBg: string; // Title color on bg (headers)
    titleOnCard: string; // Title color on card (card titles, table headers)
    secondaryOnBg: string; // Secondary brand color on bg
  };
}

export interface Theme extends DefaultTheme {
  palette: DefaultTheme["palette"] & {
    colors: typeof Colors;
    custom: CustomThemeExtension;
  };
}

// ============================================
// THEME FACTORY
// ============================================
export const drawerWidth = 256;
export const drawerWidthCollapsed = 72;

export function createAppTheme(
  mode: ThemeMode = "light",
  customPalette?: Partial<CustomPalette>,
  customDarkBg?: string
): Theme {
  const isDark = mode === "dark";
  const basePalette = isDark ? defaultDarkPalette : defaultLightPalette;
  const palette: CustomPalette = { ...basePalette, ...customPalette };

  // Always enforce 12% opacity on border color
  palette.border = deriveBorderColor(palette.border);

  // Derive additional colors
  const textSecondary = deriveSecondaryText(palette.text);

  // Preferred title color: accent in dark mode, brandPrimary in light mode
  const preferredTitleColor = isDark ? palette.accent : palette.brandPrimary;

  const derived = {
    textSecondary,
    shadow: {
      xs: deriveShadow(isDark, "xs"),
      sm: deriveShadow(isDark, "sm"),
      md: deriveShadow(isDark, "md"),
      lg: deriveShadow(isDark, "lg"),
    },
    brandPrimaryLight: deriveBrandLight(palette.brandPrimary),
    brandSecondaryLight: deriveBrandLight(palette.brandSecondary),
    // Auto-calculated readable text colors for each surface
    onBg: getContrastText(palette.bg),
    onCard: getContrastText(palette.card),
    onAccent: getContrastText(palette.accent),
    onPrimary: getContrastText(palette.brandPrimary),
    onSecondary: getContrastText(palette.brandSecondary),
    // Semantic colors with 3:1 contrast fallback to text colors
    accentOnBg:
      getContrastRatio(palette.accent, palette.bg) >= 3
        ? palette.accent
        : palette.text,
    accentOnCard:
      getContrastRatio(palette.accent, palette.card) >= 3
        ? palette.accent
        : palette.text,
    titleOnBg:
      getContrastRatio(preferredTitleColor, palette.bg) >= 3
        ? preferredTitleColor
        : palette.text,
    titleOnCard:
      getContrastRatio(preferredTitleColor, palette.card) >= 3
        ? preferredTitleColor
        : palette.text,
    secondaryOnBg:
      getContrastRatio(palette.brandSecondary, palette.bg) >= 3
        ? palette.brandSecondary
        : textSecondary,
  };

  let baseTheme = createTheme({
    typography: {
      fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    },
    palette: {
      type: mode,
      primary: {
        light: lighten(palette.accent, 0.3),
        main: palette.accent,
        dark: palette.brandPrimary,
      },
      secondary: {
        main: palette.brandSecondary,
        light: derived.brandSecondaryLight,
      },
      error: {
        main: errorColor,
      },
      warning: {
        light: lighten(warningColor, 0.15),
        main: warningColor,
        dark: darken(warningColor, 0.12),
        contrastText: getContrastText(warningColor),
      },
      background: {
        default: palette.bg,
        paper: palette.card,
      },
      text: {
        primary: palette.text,
        secondary: derived.textSecondary,
      },
      divider: palette.border,
      colors: Colors,
      custom: {
        mode,
        appearance: customPalette ? "custom" : "default",
        customDarkBg: customDarkBg || defaultDarkPalette.bg,
        custom: palette,
        derived,
      },
    },
    shape: {
      borderRadius: 8,
    },
  } as ThemeOptions);

  // Add overrides
  baseTheme = {
    ...baseTheme,
    overrides: {
      MuiDrawer: {
        paper: {
          backgroundColor: palette.card,
        },
      },
      MuiButton: {
        root: {
          color: palette.text,
        },
        label: {
          textTransform: "none",
        },
        contained: {
          boxShadow: "none",
          "&:active": {
            boxShadow: "none",
          },
        },
        containedPrimary: (() => {
          if (isDark) {
            return {
              backgroundColor: "transparent",
              color: palette.accent,
              border: `1px solid ${palette.accent}`,
              "&:hover": {
                backgroundColor: alpha(palette.accent, 0.08),
                color: palette.accent,
              },
              "&.Mui-disabled": {
                border: "none",
              },
            };
          }
          const hoverBg = darken(palette.accent, 0.1);
          return {
            backgroundColor: palette.accent,
            color: derived.onAccent,
            "&:hover": {
              backgroundColor: hoverBg,
              color: getContrastText(hoverBg),
            },
          };
        })(),
        containedSecondary: (() => {
          if (isDark) {
            const hoverBg = lighten(palette.brandSecondary, 0.2);
            return {
              backgroundColor: palette.brandSecondary,
              color: derived.onSecondary,
              "&:hover": {
                backgroundColor: hoverBg,
                color: getContrastText(hoverBg),
              },
            };
          }
          const hoverBg = darken(palette.brandSecondary, 0.1);
          return {
            backgroundColor: palette.brandSecondary,
            color: derived.onSecondary,
            "&:hover": {
              backgroundColor: hoverBg,
              color: getContrastText(hoverBg),
            },
          };
        })(),
        outlined: {
          borderColor: palette.border,
          color: palette.text,
        },
        outlinedPrimary: {
          color: derived.accentOnBg,
          borderColor: derived.accentOnBg,
        },
        text: {
          color: palette.text,
        },
        textPrimary: {
          color: derived.accentOnBg,
        },
        textSecondary: {
          color: derived.secondaryOnBg,
        },
      },
      MuiTabs: {
        root: {
          marginLeft: baseTheme.spacing(1),
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: baseTheme.palette.common.white,
        },
      },
      MuiTab: {
        root: {
          textTransform: "none",
          margin: "0 16px",
          minWidth: 0,
          padding: 0,
          [baseTheme.breakpoints.up("md")]: {
            padding: 0,
            minWidth: 0,
          },
        },
      },
      MuiIconButton: {
        root: {
          padding: baseTheme.spacing(1),
        },
      },
      MuiTooltip: {
        tooltip: {
          borderRadius: 4,
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: isDark ? palette.border : "#404854",
        },
      },
      MuiListItemText: {
        primary: {
          fontWeight: 500,
        },
      },
      MuiListItemIcon: {
        root: {
          color: "inherit",
          marginRight: 0,
          "& svg": {
            fontSize: 20,
          },
        },
      },
      MuiAvatar: {
        root: {
          width: 32,
          height: 32,
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: palette.card,
        },
      },
      MuiCard: {
        root: {
          backgroundColor: palette.card,
        },
      },
      MuiTableCell: {
        root: {
          borderBottom: `1px solid ${palette.border}`,
          color: palette.text,
        },
        head: {
          backgroundColor: palette.card,
          color: palette.text,
          fontWeight: 700,
        },
      },
      MuiInputBase: {
        root: {
          color: palette.text,
        },
        input: {
          "&::placeholder": {
            color: baseTheme.palette.text.secondary,
            opacity: 1,
          },
        },
      },
      MuiInputLabel: {
        root: {
          color: baseTheme.palette.text.secondary,
          "&.Mui-focused": {
            color: palette.accent,
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.accent,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.accent,
          },
        },
        notchedOutline: {
          borderColor: palette.border,
        },
      },
      MuiInput: {
        underline: {
          "&:before": {
            borderBottomColor: palette.border,
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottomColor: palette.accent,
          },
          "&:after": {
            borderBottomColor: palette.accent,
          },
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: baseTheme.palette.action.hover,
          "&:hover": {
            backgroundColor: baseTheme.palette.action.selected,
          },
          "&.Mui-focused": {
            backgroundColor: baseTheme.palette.action.hover,
          },
        },
        underline: {
          "&:before": {
            borderBottomColor: palette.border,
          },
          "&:after": {
            borderBottomColor: palette.accent,
          },
        },
      },
      MuiRadio: {
        root: {
          color: baseTheme.palette.text.secondary,
        },
        colorPrimary: {
          "&.Mui-checked": {
            color: palette.accent,
          },
        },
        colorSecondary: {
          "&.Mui-checked": {
            color: palette.accent,
          },
        },
      },
      MuiCheckbox: {
        root: {
          color: baseTheme.palette.text.secondary,
        },
        colorPrimary: {
          "&.Mui-checked": {
            color: palette.accent,
          },
        },
        colorSecondary: {
          "&.Mui-checked": {
            color: palette.accent,
          },
        },
      },
      MuiSwitch: {
        colorPrimary: {
          "&.Mui-checked": {
            color: palette.accent,
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: palette.accent,
          },
        },
        colorSecondary: {
          "&.Mui-checked": {
            color: palette.accent,
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: palette.accent,
          },
        },
      },
      MuiFormControlLabel: {
        label: {
          color: palette.text,
        },
      },
      MuiFormLabel: {
        root: {
          color: palette.text,
          "&.Mui-focused": {
            color: palette.text,
          },
        },
      },
      MuiLink: {
        root: {
          color: derived.accentOnBg,
        },
      },
    },
    props: {
      MuiTab: {
        disableRipple: true,
      },
    },
    mixins: {
      ...baseTheme.mixins,
      toolbar: {
        minHeight: 48,
      },
    },
  };

  return baseTheme as Theme;
}

// ============================================
// DEFAULT THEME EXPORT (light mode)
// ============================================
export let theme = createAppTheme("light");

export default theme;
